import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Star, X, Upload, Search, Filter } from 'lucide-react';
import { testimonialsService, mediaService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [filterRating, setFilterRating] = useState('all'); // all, 5, 4, 3, 2, 1
  const [filterFeatured, setFilterFeatured] = useState('all'); // all, featured, not_featured
  const [formData, setFormData] = useState({
    client_name: '',
    position: '',
    company: '',
    testimonial: '',
    rating: 5,
    image_url: '',
    is_featured: false
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  useEffect(() => {
    filterTestimonials();
  }, [testimonials, searchQuery, filterStatus, filterRating, filterFeatured]);

  const filterTestimonials = () => {
    let filtered = [...testimonials];

    // Recherche par nom, poste, entreprise ou contenu
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        getDisplayName(t).toLowerCase().includes(query) ||
        (t.position || '').toLowerCase().includes(query) ||
        (t.company || '').toLowerCase().includes(query) ||
        getDisplayTestimonial(t).toLowerCase().includes(query)
      );
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => 
        filterStatus === 'active' ? t.is_active : !t.is_active
      );
    }

    // Filtre par note
    if (filterRating !== 'all') {
      filtered = filtered.filter(t => t.rating === parseInt(filterRating));
    }

    // Filtre par featured
    if (filterFeatured !== 'all') {
      filtered = filtered.filter(t => 
        filterFeatured === 'featured' ? t.is_featured : !t.is_featured
      );
    }

    setFilteredTestimonials(filtered);
  };

  const loadTestimonials = async () => {
    try {
      const response = await testimonialsService.getAll({ active: 'false' });
      console.log('Témoignages chargés:', response.data.testimonials);
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (testimonial = null) => {
    if (testimonial) {
      setEditing(testimonial);
      console.log('Ouverture modal avec:', testimonial);
      
      // Support pour les deux structures de données (ancienne et nouvelle)
      setFormData({
        client_name: testimonial.client_name || testimonial.name || '',
        position: testimonial.position || '',
        company: testimonial.company || '',
        testimonial: testimonial.testimonial || testimonial.content || '',
        rating: testimonial.rating || 5,
        image_url: testimonial.image_url || '',
        is_featured: testimonial.is_featured ? true : false
      });
    } else {
      setEditing(null);
      setFormData({
        client_name: '',
        position: '',
        company: '',
        testimonial: '',
        rating: 5,
        image_url: '',
        is_featured: false
      });
    }
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    try {
      const response = await mediaService.uploadImage(file);
      const BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
      const imageUrl = `${BASE_URL}${response.data.media.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Données envoyées:', formData);
    
    try {
      if (editing) {
        await testimonialsService.update(editing.id, formData);
        toast.success('Témoignage mis à jour');
      } else {
        await testimonialsService.create(formData);
        toast.success('Témoignage créé');
      }
      setShowModal(false);
      loadTestimonials();
    } catch (error) {
      console.error('Erreur complète:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce témoignage ?')) return;
    
    try {
      await testimonialsService.delete(id);
      toast.success('Témoignage supprimé');
      loadTestimonials();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (testimonial) => {
    try {
      await testimonialsService.update(testimonial.id, { is_active: !testimonial.is_active });
      toast.success('Statut mis à jour');
      loadTestimonials();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Fonction pour obtenir le nom à afficher (support ancienne/nouvelle structure)
  const getDisplayName = (testimonial) => testimonial.client_name || testimonial.name || 'Anonyme';
  const getDisplayTestimonial = (testimonial) => testimonial.testimonial || testimonial.content || '';

  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterRating('all');
    setFilterFeatured('all');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Témoignages</h1>
          <p className="text-dark-500">Gérez les avis de vos clients</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Nouveau témoignage
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card p-6 mb-6">
        <div className="grid md:grid-cols-4 gap-4">
          {/* Recherche */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom, poste, entreprise..."
                className="input pl-10"
              />
            </div>
          </div>

          {/* Filtre Statut */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>

          {/* Filtre Note */}
          <div>
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="input"
            >
              <option value="all">Toutes les notes</option>
              <option value="5">⭐⭐⭐⭐⭐ (5)</option>
              <option value="4">⭐⭐⭐⭐ (4)</option>
              <option value="3">⭐⭐⭐ (3)</option>
              <option value="2">⭐⭐ (2)</option>
              <option value="1">⭐ (1)</option>
            </select>
          </div>
        </div>

        {/* Filtres supplémentaires */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-100">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setFilterFeatured(filterFeatured === 'featured' ? 'all' : 'featured')}
              className={`badge cursor-pointer ${
                filterFeatured === 'featured' 
                  ? 'bg-yellow-100 text-yellow-700' 
                  : 'bg-dark-100 text-dark-500'
              }`}
            >
              <Star className="w-3 h-3 mr-1" />
              Mis en avant
            </button>

            {(searchQuery || filterStatus !== 'all' || filterRating !== 'all' || filterFeatured !== 'all') && (
              <button
                onClick={resetFilters}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>

          <div className="text-sm text-dark-500">
            {filteredTestimonials.length} témoignage{filteredTestimonials.length > 1 ? 's' : ''} affiché{filteredTestimonials.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {filteredTestimonials.length === 0 ? (
        <div className="card p-12 text-center">
          <Star className="w-16 h-16 text-dark-300 mx-auto mb-4" />
          <p className="text-dark-500 mb-4">
            {testimonials.length === 0 
              ? 'Aucun témoignage' 
              : 'Aucun résultat pour ces critères'}
          </p>
          {testimonials.length === 0 ? (
            <button onClick={() => openModal()} className="btn-primary">
              Ajouter un témoignage
            </button>
          ) : (
            <button onClick={resetFilters} className="btn-ghost">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTestimonials.map((testimonial) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 relative group"
            >
              {/* Actions */}
              <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openModal(testimonial)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-dark-50"
                >
                  <Edit className="w-4 h-4 text-dark-600" />
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="p-2 bg-white rounded-lg shadow hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex items-center gap-4 mb-4">
                {testimonial.image_url ? (
                  <img
                    src={testimonial.image_url}
                    alt={getDisplayName(testimonial)}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {getDisplayName(testimonial).charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-dark-900">{getDisplayName(testimonial)}</h3>
                  <p className="text-sm text-dark-500">{testimonial.position}</p>
                  <p className="text-sm text-primary-600">{testimonial.company}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-dark-200'}`}
                  />
                ))}
              </div>

              {/* Quote */}
              <p className="text-dark-600 text-sm line-clamp-4">{getDisplayTestimonial(testimonial)}</p>

              {/* Status */}
              <div className="mt-4 pt-4 border-t border-dark-100 flex items-center justify-between">
                <button
                  onClick={() => toggleActive(testimonial)}
                  className={`badge cursor-pointer ${testimonial.is_active ? 'badge-secondary' : 'bg-dark-100 text-dark-500'}`}
                >
                  {testimonial.is_active ? 'Actif' : 'Inactif'}
                </button>
                {testimonial.is_featured && (
                  <span className="badge bg-yellow-100 text-yellow-700">
                    <Star className="w-3 h-3 mr-1" />
                    Mis en avant
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-dark-900/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-dark-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">
                {editing ? 'Modifier le témoignage' : 'Nouveau témoignage'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-dark-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Photo */}
              <div>
                <label className="label">Photo</label>
                <div className="flex items-center gap-4">
                  {formData.image_url ? (
                    <div className="relative">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="w-20 h-20 border-2 border-dashed border-dark-200 rounded-full flex items-center justify-center cursor-pointer hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                      {uploading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Upload className="w-6 h-6 text-dark-400" />
                      )}
                    </label>
                  )}
                  <div className="text-sm text-dark-500">
                    <p>Photo du client</p>
                    <p>JPG, PNG (max 5MB)</p>
                  </div>
                </div>
              </div>

              {/* Nom */}
              <div>
                <label className="label">Nom complet *</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                  className="input"
                  required
                  placeholder="Ex: Dr. Amadou Diallo"
                />
              </div>

              {/* Position */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Poste</label>
                  <input
                    type="text"
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                    className="input"
                    placeholder="Ex: Directeur"
                  />
                </div>
                <div>
                  <label className="label">Entreprise</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    className="input"
                    placeholder="Ex: BNP Paribas"
                  />
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="label">Note</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= formData.rating
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-dark-200 hover:text-yellow-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Contenu */}
              <div>
                <label className="label">Témoignage *</label>
                <textarea
                  value={formData.testimonial}
                  onChange={(e) => setFormData(prev => ({ ...prev, testimonial: e.target.value }))}
                  className="input"
                  rows={4}
                  required
                  placeholder="Ce que le client dit de vous..."
                />
              </div>

              {/* Featured */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  className="w-4 h-4 rounded border-dark-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_featured" className="text-dark-700">
                  Mettre en avant sur la page d'accueil
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-dark-100">
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default TestimonialsPage;